/**
 * **Kestner, seated, without the hat.** Beat 11's figure, and the close one.
 *
 * `docs/beat-11-plan.md` is the brief and three of its sentences decide every
 * number here:
 *
 * 1. *"Nobody does paperwork in a wide-brimmed oilskin hat, and the hat is the
 *    role."* So no hat and no cape — shirtsleeves, rolled — and both hang on the
 *    hook by the door instead (`kestnerHatAndCape`). His 64 px read was brim,
 *    stoop, head angle; the brim is on the wall, so **the stoop has to carry
 *    it**, and the waist fold below is the largest rotation on him.
 * 2. *"A face whose hands are the brightest thing in frame is a man working."*
 *    The sleeves are rolled so the forearms are skin: under a lamp that sits
 *    below his eyeline, the four pale marks in the pool are two forearms and two
 *    hands, and the face above them is out of the light. There is still nothing
 *    on the face — D-002, and `kestner.ts` rule 2. **The glasses are the eye.**
 * 3. *"He takes his glasses off and presses the bridge of his nose."* That needs
 *    a live elbow, a live shoulder and glasses that can change parent, and it is
 *    why this is a variant rather than the standing mesh with its pivots
 *    restored: the standing figure's keep list is shared with beat 6's yard, and
 *    `docs/backlog.md` prices its pivots at a frame beat 6 cannot afford. **This
 *    asset is spawned in the office only**, so its pivots are paid for there.
 *
 * **Sides are anatomical in this file.** He faces +Z, so his RIGHT hand is at
 * −X. (`kestner.ts` names its +X leg "right"; that mesh is a standing, merged
 * silhouette where it never mattered, and it is left alone rather than renamed
 * under beat 6.) It matters here because the lamp stands at his left, which is
 * where a right-handed man puts one — the writing hand casts no shadow on the
 * page, and the gesture is made with the hand that is already in the light.
 *
 * The rest pose is solved, not eyeballed: `reachSeatedArm()` puts each hand where the
 * desk is, from the chair's own numbers in `src/world/office.ts`. A hand that
 * floats over a page is the thing no gate can see — `kestner.ts` records Alex
 * finding exactly that on the standing figure's clipboard.
 */
import * as THREE from 'three';
import { collapseRigged } from '@/world/instancing';
import { surface } from '../materials';
import type { AssetDefinition } from '../types';
import { addBoot, kestnerMaterials, part } from './kestner';

/**
 * Where his hip axis is, in the asset's own frame. `office.ts` seats him with
 * this: the chair's seat top is at y 0.495 and a thigh is ~0.09 m thick under
 * its axis, so the hip is 0.585 up. `z` is not anatomy, it is the centring
 * rule: knees and boots reach forward of the hip much further than the pelvis
 * reaches back, so the whole figure is authored 0.205 m behind its origin to put
 * the mesh's Z centre on it (`asset.spec.ts`, 0.03 m tolerance).
 */
export const KESTNER_SEATED_HIP = { y: 0.585, z: -0.205 } as const;

/** Standing `leg()`'s own lengths, which the seated leg reuses unchanged. */
const THIGH = 0.42;
/** Knee to the sole's underside: shin centre −0.19 … boot sole −0.474 −0.006. */
const SHIN_TO_SOLE = 0.48;
/**
 * How far the thigh slopes down from the hip to the knee.
 *
 * The chair is 0.495 m to the seat and his shin-plus-boot is 0.48 m, so a level
 * thigh leaves the boots 105 mm off the floor — the figure floats and the asset
 * gate says so. Sloping the thigh until the knee is exactly SHIN_TO_SOLE up puts
 * the soles on the floor with a vertical shin. Derived rather than typed so the
 * seat height can move without the feet quietly leaving the ground.
 */
const THIGH_SLOPE = Math.asin((KESTNER_SEATED_HIP.y - SHIN_TO_SOLE) / THIGH);

/**
 * The fold. The standing figure tips 0.1 rad; this one 0.42, folded over the
 * page — plan lever 1, "folded over the desk, not squared at it".
 */
export const SEATED_WAIST_FOLD = 0.42;
const WAIST = { y: 0.66, z: -0.05 } as const;
/** In the waist frame. Toward the back of the ribcage, as on the standing figure. */
const SHOULDER = { x: 0.22, y: 0.44, z: -0.02 } as const;
/** Head down at the page, on top of the fold: 0.70 rad in all. */
export const SEATED_HEAD_REST = 0.28;

const UPPER_ARM = 0.28;
/** Elbow to the centre of the hand. */
const FOREARM_TO_HAND = 0.27;

/**
 * Where the hands rest, in the asset frame with the hip at the origin of Z.
 *
 * The desk top is y 0.75 in the room and its near edge is 0.19 m in front of his
 * hip. The right hand is ON the page, holding the pencil; the left forearm lies
 * along the desk and its hand pins the sheet — "one forearm flat on the page
 * holding it still". Hand boxes are 0.1 m long, so a centre at 0.79 is a hand
 * resting on the surface rather than in it.
 */
const HAND_REST = {
  right: { x: -0.09, y: 0.79, z: 0.43 },
  left: { x: 0.13, y: 0.785, z: 0.36 },
} as const;

/**
 * The exact two-bone solve for an arm whose shoulder rotates in Euler XYZ order
 * (Z applied to the arm first, then X) and whose elbow bends about its own X.
 *
 * The elbow angle depends only on how far the target is. The folded arm is then
 * a vector in its own Y/Z plane, `(0, −a, c)`; Z swings it sideways, giving
 * `x = a·sin θ`, and X turns what remains in the Y/Z plane onto the target.
 * It was a planar solve with the sideways swing guessed on top, and that was
 * fine for a hand on the desk and 14 cm wrong for a hand at his face — the
 * more an arm folds, the less a sideways swing moves the hand.
 *
 * Exported because the glasses gesture (`src/anim/kestnerDesk.ts`) reaches for
 * his face with this arm, and a second solver would be a second idea of how
 * long his forearm is.
 */
export function reachSeatedArm(
  shoulder: THREE.Vector3,
  target: THREE.Vector3,
): { shoulderX: number; shoulderZ: number; elbowX: number } {
  const dx = target.x - shoulder.x;
  const dy = target.y - shoulder.y;
  const dz = target.z - shoulder.z;
  const d = Math.min(Math.hypot(dx, dy, dz), UPPER_ARM + FOREARM_TO_HAND - 1e-4);
  const cosE = (d ** 2 - UPPER_ARM ** 2 - FOREARM_TO_HAND ** 2) / (2 * UPPER_ARM * FOREARM_TO_HAND);
  // Negative: the forearm folds forward (+Z), as `kestner.ts`'s elbows do.
  const elbowX = -Math.acos(Math.max(-1, Math.min(1, cosE)));
  const a = UPPER_ARM + FOREARM_TO_HAND * Math.cos(elbowX);
  const c = -FOREARM_TO_HAND * Math.sin(elbowX);
  const shoulderZ = Math.asin(Math.max(-1, Math.min(1, dx / a)));
  const shoulderX = Math.atan2(dz, dy) - Math.atan2(c, -a * Math.cos(shoulderZ));
  return { shoulderX, shoulderZ, elbowX };
}

/** Hip-relative point (as `HAND_REST` is authored) → waist frame, which only rotates about X. */
function toWaist(p: { x: number; y: number; z: number }): THREE.Vector3 {
  const y = p.y - WAIST.y;
  const z = p.z - WAIST.z;
  const c = Math.cos(-SEATED_WAIST_FOLD);
  const s = Math.sin(-SEATED_WAIST_FOLD);
  return new THREE.Vector3(p.x, y * c - z * s, y * s + z * c);
}

function seatedLeg(side: 'left' | 'right', x: number, m: ReturnType<typeof kestnerMaterials>): THREE.Group {
  const hip = new THREE.Group();
  hip.name = `kestner-seated-hip-${side}`;
  hip.position.set(x, KESTNER_SEATED_HIP.y, KESTNER_SEATED_HIP.z);
  hip.rotation.x = -(Math.PI / 2 - THIGH_SLOPE);

  const upper = part(new THREE.CylinderGeometry(0.1, 0.115, THIGH, 12), m.trouser, `${side}-thigh`);
  upper.position.set(0, -THIGH / 2, 0);
  hip.add(upper);

  // The knee undoes the hip's swing so the shin hangs plumb. Inside it, an
  // offset of +THIGH puts the standing leg's hip-relative numbers back into
  // use, so the boots are `addBoot`'s boots and not a seated copy of them.
  const knee = new THREE.Group();
  knee.position.set(0, -THIGH, 0);
  knee.rotation.x = Math.PI / 2 - THIGH_SLOPE;
  hip.add(knee);
  const lowerFrame = new THREE.Group();
  lowerFrame.position.set(0, THIGH, 0);
  knee.add(lowerFrame);
  const lower = part(new THREE.CylinderGeometry(0.085, 0.095, 0.4, 12), m.trouser, `${side}-shin`);
  lower.position.set(0, -0.61, 0);
  lowerFrame.add(lower);
  // Soles in the boot's own material: under a desk nobody sees a sole, and a
  // separate one is a material group, which is three draw calls a frame.
  addBoot(lowerFrame, side, m.boot, m.boot);
  return hip;
}

function seatedArm(
  side: 'left' | 'right',
  shirt: THREE.Material,
  skin: THREE.Material,
): { shoulder: THREE.Group; elbow: THREE.Group; hand: THREE.Mesh } {
  const sign = side === 'right' ? -1 : 1;
  const shoulder = new THREE.Group();
  shoulder.name = `kestner-shoulder-${side}`;
  shoulder.position.set(sign * SHOULDER.x, SHOULDER.y, SHOULDER.z);
  const solved = reachSeatedArm(shoulder.position, toWaist(HAND_REST[side]));
  shoulder.rotation.set(solved.shoulderX, 0, solved.shoulderZ);

  const upper = part(new THREE.CylinderGeometry(0.056, 0.062, UPPER_ARM, 10), shirt, `${side}-sleeve`);
  upper.position.set(0, -UPPER_ARM / 2, 0);
  shoulder.add(upper);

  const elbow = new THREE.Group();
  elbow.name = `kestner-elbow-${side}`;
  elbow.position.set(0, -UPPER_ARM, 0);
  elbow.rotation.x = solved.elbowX;
  shoulder.add(elbow);

  // The roll of the sleeve, pushed up to the elbow. It is the whole of
  // "shirtsleeves": one band of cloth, and below it the arm is skin.
  const roll = part(new THREE.CylinderGeometry(0.066, 0.066, 0.05, 10), shirt, `${side}-sleeve-roll`);
  roll.position.set(0, -0.02, 0);
  elbow.add(roll);
  const forearm = part(new THREE.CylinderGeometry(0.043, 0.05, 0.22, 10), skin, `${side}-forearm`);
  forearm.position.set(0, -0.14, 0);
  elbow.add(forearm);
  const hand = part(new THREE.BoxGeometry(0.075, 0.1, 0.045), skin, `${side}-hand`);
  hand.name = `kestner-hand-${side}`;
  hand.position.set(0, -FOREARM_TO_HAND, 0);
  elbow.add(hand);
  return { shoulder, elbow, hand };
}

/**
 * The glasses: two lenses, a bridge and two arms, on a pivot of their own so
 * the gesture can move them from his face to his hand (`attach`, which keeps
 * the world pose). Built at the face; `GLASSES_ON_FACE` is that local pose, so
 * putting them back is a known transform rather than a remembered one.
 */
export const GLASSES_ON_FACE = { x: 0, y: 0.163, z: 0.108 } as const;

function glasses(): THREE.Group {
  // One material, pale enough to catch the lamp and read as glass, nowhere
  // near paper-white. The frame was a second, dark one, and at the distance
  // the glasses are ever seen from it was invisible and cost a material group.
  const lens = surface('mineral', 0xa9a79d);
  const frame = lens;
  const group = new THREE.Group();
  group.name = 'kestner-glasses';
  group.position.set(GLASSES_ON_FACE.x, GLASSES_ON_FACE.y, GLASSES_ON_FACE.z);
  for (const x of [-0.028, 0.028]) {
    const pane = part(new THREE.BoxGeometry(0.04, 0.027, 0.004), lens, 'kestner-glasses-lens');
    pane.position.set(x, 0, 0);
    group.add(pane);
  }
  const bridge = part(new THREE.BoxGeometry(0.016, 0.005, 0.005), frame, 'kestner-glasses-bridge');
  bridge.position.set(0, 0.008, 0);
  group.add(bridge);
  for (const x of [-0.051, 0.051]) {
    const temple = part(new THREE.BoxGeometry(0.004, 0.004, 0.11), frame, 'kestner-glasses-temple');
    temple.position.set(x, 0.006, -0.055);
    group.add(temple);
  }
  return group;
}

function head(skin: THREE.Material, hairMaterial: THREE.Material): { neck: THREE.Group; specs: THREE.Group } {
  const neck = new THREE.Group();
  neck.name = 'kestner-neck';
  neck.position.set(0, 0.5, 0.01);
  neck.rotation.x = SEATED_HEAD_REST;

  const column = part(new THREE.CylinderGeometry(0.055, 0.06, 0.09, 10), skin, 'kestner-neck-column');
  column.position.set(0, 0.035, 0);
  neck.add(column);
  const skull = part(new THREE.SphereGeometry(1, 12, 8), skin, 'kestner-head');
  skull.scale.set(0.104, 0.126, 0.113);
  skull.position.set(0, 0.145, 0);
  neck.add(skull);
  const jaw = part(new THREE.BoxGeometry(0.098, 0.062, 0.095), skin, 'kestner-jaw');
  jaw.position.set(0, 0.085, 0.045);
  neck.add(jaw);
  const face = part(new THREE.BoxGeometry(0.112, 0.105, 0.028), skin, 'kestner-face');
  face.position.set(0, 0.15, 0.09);
  neck.add(face);

  // **Hair, because the hat is off.** Under the brim a bare sphere was never
  // seen; out from under it, a bald skin sphere reads as a mannequin — the thing
  // `kestner.ts`'s jaw exists to prevent. A dark cap over the crown and back,
  // stopping above the face plane, is what makes the head a person's.
  const hair = part(new THREE.SphereGeometry(1, 12, 8, 0, Math.PI * 2, 0, Math.PI * 0.62), hairMaterial, 'kestner-hair');
  // Centred on the skull and round in Y/Z, larger than the skull's own Y/Z
  // semi-axes (0.126, 0.113), so tipping it back cannot let the skull through.
  // It first had an ellipsoid of the skull's own shape, offset and rotated,
  // and the skull poked out of the crown as a pale lightning bolt in every
  // lit frame. Tipped back so the hairline sits high on the forehead and the
  // back of the head is covered — before that it read as a beanie.
  hair.scale.set(0.11, 0.132, 0.13);
  hair.position.set(0, 0.145, 0);
  hair.rotation.x = -0.55;
  neck.add(hair);

  const specs = glasses();
  neck.add(specs);
  return { neck, specs };
}

export interface KestnerSeatedParts {
  readonly waist: THREE.Object3D;
  readonly neck: THREE.Object3D;
  readonly shoulderLeft: THREE.Object3D;
  readonly shoulderRight: THREE.Object3D;
  readonly elbowLeft: THREE.Object3D;
  /** Reparented between `neck` and the left hand by the gesture. */
  readonly glasses: THREE.Object3D;
}

function build(): THREE.Group {
  const m = kestnerMaterials();
  // A dull work shirt, warm like the oilskin and a step lighter than it. Not
  // white: under the lamp a white shirt would be the brightest thing in frame,
  // and that is the hands' job.
  const shirt = surface('organic', 0x5b5545);
  const hair = surface('organic', 0x423b33);
  const pencil = surface('organic', 0x6b5a3a);

  const group = new THREE.Group();
  group.name = 'kestner-seated';

  const hipZ = KESTNER_SEATED_HIP.z;
  group.add(seatedLeg('right', -0.11, m), seatedLeg('left', 0.11, m));
  const pelvis = part(new THREE.BoxGeometry(0.36, 0.2, 0.28), m.trouser, 'kestner-pelvis');
  pelvis.position.set(0, KESTNER_SEATED_HIP.y + 0.01, hipZ - 0.03);
  group.add(pelvis);

  const waist = new THREE.Group();
  waist.name = 'kestner-waist';
  waist.position.set(0, WAIST.y, hipZ + WAIST.z);
  waist.rotation.x = SEATED_WAIST_FOLD;
  group.add(waist);

  // ── The torso is one capsule, and it was two boxes ────────────────────────
  //
  // A chest box over a belly box is what the standing figure has, under a cape
  // that hides it. Out of the cape and folded 0.42 rad, the chest box's lower
  // front edge stood proud of his lap like a shelf and the pair read as a
  // sandwich board. A capsule has no edge to stand proud, and a rounded back is
  // also simply what a tired man's back looks like from behind, which is where
  // she first sees it.
  const trunk = part(new THREE.CapsuleGeometry(0.125, 0.3, 4, 12), shirt, 'kestner-trunk');
  trunk.scale.set(1.42, 1, 1);
  trunk.position.set(0, 0.2, 0);
  waist.add(trunk);
  // Shoulders as a bar with round ends, not a yoke with corners.
  const yoke = part(new THREE.CapsuleGeometry(0.065, 0.3, 4, 8), shirt, 'kestner-shoulders');
  yoke.rotation.z = Math.PI / 2;
  yoke.position.set(0, SHOULDER.y - 0.01, SHOULDER.z);
  waist.add(yoke);

  const right = seatedArm('right', shirt, m.skin);
  const left = seatedArm('left', shirt, m.skin);
  waist.add(right.shoulder, left.shoulder);

  // The pencil, in the writing hand, and the reason the right hand is on the
  // page at all. Tipped back toward him, as a held pencil is.
  const pen = part(new THREE.CylinderGeometry(0.004, 0.004, 0.14, 6), pencil, 'kestner-pencil');
  pen.position.set(0, -0.02, 0.03);
  pen.rotation.x = 0.9;
  right.hand.add(pen);

  const { neck, specs } = head(m.skin, hair);
  waist.add(neck);

  // Six live nodes, and every one of them is a verb: the fold and the head
  // are the look-up, both shoulders are the writing and the resting, the left
  // elbow is the hand to the face, and the glasses change parent. The right
  // elbow is not live — the writing is a shoulder motion — and the legs never
  // move, so they merge into the root with the pelvis.
  collapseRigged(group, [waist, neck, right.shoulder, left.shoulder, left.elbow, specs]);
  return group;
}

function required(root: THREE.Object3D, name: string): THREE.Object3D {
  const found = root.getObjectByName(name);
  if (!found) throw new Error(`kestner-seated: rig node "${name}" missing — was the mesh renamed?`);
  return found;
}

export function findKestnerSeatedParts(root: THREE.Object3D): KestnerSeatedParts {
  return {
    waist: required(root, 'kestner-waist'),
    neck: required(root, 'kestner-neck'),
    shoulderLeft: required(root, 'kestner-shoulder-left'),
    shoulderRight: required(root, 'kestner-shoulder-right'),
    elbowLeft: required(root, 'kestner-elbow-left'),
    glasses: required(root, 'kestner-glasses'),
  };
}

export const kestnerSeatedAsset: AssetDefinition = {
  id: 'kestner-seated',
  label: 'Kestner (seated)',
  description: 'Beat 11: at the desk, hatless, sleeves rolled, glasses on. The stoop carries him.',
  footprint: { width: 0.56, height: 1.41, length: 0.76 },
  create: build,
};
